import { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, Divider, Typography } from '@vapor/material'
import FormLayoutDrawer from 'src/app/layout/form-layout/form-layout-drawer'
import { useTranslation } from 'react-i18next'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { PaymentActivesQuery, ReferenceCustomerFragment, useCashDesksQuery, useUnpaidDocumentsQuery } from 'src/api/api.generated'
import usePaymentDocumentForm, { PaymentDocumentFormSchema } from './paid-in-full-document-form.hook'
import { Controller } from 'react-hook-form'
import DocumentCustomerInput from 'src/app/components/document-customer-input/document-customer-input'
import { DocumentCustomerFormSchema } from 'src/app/components/document-customer-input/document-customer-schema'
import PaymentSimpleInput from 'src/app/components/payment/payment-simple-input/payment-simple-input'
import { Payment } from 'src/app/components/payment/payment-splitter/payment-splitter.types'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { HICTitleSmall } from 'src/app/components/typography/title-small'
import { PaymentDistribution } from 'src/app/components/payment/payment-distribution/payment-distribution'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { usePaidInFullDocumentCRUD } from './paid-in-full-document.crud.hook'
import { PaidInFullPendingDocumentsGrid, PaidInFullPendingDocumentsRow } from './paid-in-full-pending-documents-grid'
import { UnpaidDocumentsPaidInFullQuotasType } from 'src/app/components/payment/payment-distribution/payment-distribution.types'
import { ReferenceDocumentsList } from './reference-documents-list'
import { HICError } from 'src/app/components/typography/error-text'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { getCustomerFromReferenceCustomerFragment } from 'src/app/pages/common/customer/documents/utils'
import { isCashDeskDisabled } from 'src/app/components/cash-desk-input/cash-desk-utils'

type PaidInFullDocumentFormProps = {
  open: boolean
  tenantId: string
  propertyId: string
  /** List of payment methods */
  paymentActives: PaymentActivesQuery | undefined

  onClose: () => void
  paidInFullDocumentId?: string
  defaultCustomer?: DocumentCustomerFormSchema
  /** When true, the document will be saved as draft and an email with payment request will be sent */
  createAsDraft?: boolean
  comeFromUnpaidDocumentId?: string
  referenceCustomer: ReferenceCustomerFragment
}

export default function PaidInFullDocumentForm(props: PaidInFullDocumentFormProps) {
  const { open, tenantId, propertyId, paymentActives, onClose, defaultCustomer, paidInFullDocumentId, createAsDraft, comeFromUnpaidDocumentId, referenceCustomer } = props

  const { t } = useTranslation()
  const { md } = useBreakpoints()
  const localizeString = useLocalizeString()
  const showError = useErrorSnackBar()

  const [localCustomerId, setLocalCustomerId] = useState(defaultCustomer?.id)
  const [localDocumentId, setLocalDocumentId] = useState<string>()
  const [paidAmount, setPaidAmount] = useState(0)

  useEffect(() => {
    setLocalDocumentId(paidInFullDocumentId)
  }, [paidInFullDocumentId])

  const { data: cashDesksData, loading: cashDesksLoading } = useCashDesksQuery({
    variables: {
      tenantId,
      propertyId,
      open: true,
    },
  })
  const cashDeskId = useMemo(() => {
    return cashDesksData?.cashDesks?.find((c) => !isCashDeskDisabled(c, 'NON_FISCAL'))?.id || null
  }, [cashDesksData])

  //#region API
  const {
    dataPaidInFullDocument,
    createPaidInFullDocument,
    dataPaidInFullPendingDocuments,
    deleteDocument,
    loading: loadingPaidInFullDocumentCRUD,
  } = usePaidInFullDocumentCRUD(tenantId, propertyId, localDocumentId, localCustomerId)

  const {
    data: unpaidDocuments,
    refetch: unpaidDocumentsRefetch,
    loading: unpaidDocumentsLoading,
  } = useUnpaidDocumentsQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        customerId: { eq: props.defaultCustomer?.id },
        unpaid: true,
      },
    },
    skip: !props.defaultCustomer,
  })

  useEffect(() => {
    unpaidDocumentsRefetch({
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        customerId: { eq: localCustomerId || null },
        unpaid: true,
      },
    })
  }, [localCustomerId])

  //#endregion

  const mode: 'NEW' | 'VIEW' = useMemo(() => {
    if (localDocumentId) return 'VIEW'
    return 'NEW'
  }, [localDocumentId])

  const isDraft = useMemo(() => {
    return (dataPaidInFullDocument?.documents?.length && dataPaidInFullDocument?.documents?.[0].auditable.state === 'DRAFT') || createAsDraft
  }, [dataPaidInFullDocument, createAsDraft])

  const onSubmitHandler: (data: PaymentDocumentFormSchema, draft: boolean) => Promise<void> = useCallback(
    //
    async (data, draft) => {
      if (cashDeskId) {
        if (!data.customer.email && isDraft) {
          showError(t('pms.accounting.documents.missing_company_email'))
        } else {
          createPaidInFullDocument({
            variables: {
              input: {
                customerId: data.customer.id,
                paymentActiveId: data.payment.paymentActiveId,
                payments: data.documentPayments.map((d) => ({ referenceDocumentId: d.referenceDocumentId, amount: d.amount })),
                tenantId: tenantId,
                propertyId: propertyId,
                cashDeskId: cashDeskId,
                draft: draft,
              },
            },
            onCompleted: (data) => {
              if (data.createPaidInFullDocument) setLocalDocumentId(data.createPaidInFullDocument)
            },
            onError: (errors) => {
              showError(errors)
            },
          })
        }
      }
    },
    [cashDeskId, tenantId, propertyId, isDraft],
  )

  const documentReference = useMemo(() => {
    return dataPaidInFullDocument?.documents?.[0].numberExtended
  }, [dataPaidInFullDocument])

  const title = useMemo(() => {
    switch (mode) {
      case 'NEW':
        return isDraft ? t('pms.accounting.documents.new_payment_request_tspay') : t('pms.accounting.documents.new_payment_request')
      case 'VIEW':
        return isDraft
          ? t('pms.accounting.documents.view_payment_request_tspay', { documentReference: documentReference })
          : t('pms.accounting.documents.view_payment_request', { documentReference: documentReference })
    }
  }, [t, mode, isDraft, documentReference])

  const defaultFormValue: Partial<PaymentDocumentFormSchema> = useMemo((): Partial<PaymentDocumentFormSchema> => {
    switch (mode) {
      case 'NEW': {
        if (referenceCustomer)
          return {
            customer: getCustomerFromReferenceCustomerFragment(referenceCustomer),
          }
        return {}
      }
      case 'VIEW': {
        const document = dataPaidInFullDocument?.documents?.[0]
        const payment = document?.documentPaymentDetails?.[0]
        return {
          customer: document ? { ...document.customer, splitPayment: false } : undefined,
          payment: payment ? { ...payment, amount: document.totalAmount, editable: false } : undefined,
        }
      }
    }
  }, [mode, defaultCustomer, dataPaidInFullDocument])

  const totalUnpaidAmount = useMemo(() => {
    return unpaidDocuments?.documents?.reduce((total, document) => {
      return total + document.unpaidAmount
    }, 0)
  }, [unpaidDocuments])

  const { handleSubmit, control, errors, getValues, setValue, watch } = usePaymentDocumentForm(onSubmitHandler, defaultFormValue, totalUnpaidAmount)

  const defaultPaymentActiveWithGateway = useMemo(() => {
    if ((paymentActives?.paymentActives?.length || 0) > 0) return paymentActives?.paymentActives?.find((pa) => pa.paymentMethods?.some((pm) => pm.gateway))?.id
    return undefined
  }, [paymentActives])

  const unpaidPaymentActives = useMemo(() => {
    return paymentActives?.paymentActives?.filter((pa) => pa.paymentType.code === 'UNPAID')?.map((v) => v.id) ?? []
  }, [paymentActives])

  useEffect(() => {
    if (createAsDraft && defaultPaymentActiveWithGateway) {
      setValue('payment', { paymentActiveId: defaultPaymentActiveWithGateway, amount: 0, editable: true })
    }
  }, [createAsDraft, defaultPaymentActiveWithGateway])

  useEffect(() => {
    if (defaultFormValue) {
      if (defaultFormValue.customer) {
        setValue('customer', defaultFormValue.customer)
        setValue('customer.fiscalCode', defaultFormValue.customer.fiscalCode ?? '')
        setValue('customer.vatNumber', defaultFormValue.customer.vatNumber ?? '')
      } else {
        setValue('customer.fiscalCode', '')
        setValue('customer.vatNumber', '')
      }
      if (defaultFormValue.payment) setValue('payment', defaultFormValue.payment)
      if (defaultFormValue.documentPayments) setValue('documentPayments', defaultFormValue.documentPayments)
    }
  }, [defaultFormValue])

  const paymentActivesAllowed: Payment[] = useMemo(() => {
    return (
      paymentActives?.paymentActives
        .filter((p) => p.paymentType.sign === 'POSITIVE' && !['UNPAID', 'SPLITPAYMENT', 'DEPOSIT'].includes(p.paymentType.code))
        .map<Payment>((pa) => ({ paymentActiveId: pa.id, description: localizeString(pa.name) })) ?? []
    )
  }, [paymentActives?.paymentActives])

  const submitButtonHandler = useMemo(() => handleSubmit(isDraft ?? false), [handleSubmit, isDraft])

  const onPaymentBlurHandler = useCallback(() => {
    const amount = watch('payment.amount')
    if (amount) setPaidAmount(amount)
  }, [watch('payment.amount')])

  const loadings = useMemo(() => {
    return unpaidDocumentsLoading || loadingPaidInFullDocumentCRUD
  }, [unpaidDocumentsLoading, loadingPaidInFullDocumentCRUD])

  /**
   * Processa le righe delle eventuali quietanze in drat (cioè richieste di pagamento).
   * Produce:
   * `paidInFullPendingDocumentsRows` sono le righe per la datagrid che mostra le quietanze in draft.
   * `pendingPaidInFullDocumentQuotas` le quote delle quietanze in draft raggruppate per documento in sospeso.
   */
  const { paidInFullPendingDocumentsRows, pendingPaidInFullDocumentQuotas } = useMemo(() => {
    const pendingPaidInFullDocumentQuotas: UnpaidDocumentsPaidInFullQuotasType = {}
    const paidInFullPendingDocumentsRows =
      dataPaidInFullPendingDocuments?.documents?.map<PaidInFullPendingDocumentsRow>((d) => ({
        id: d.id,
        numberExtended: d.numberExtended,
        documentDate: d.documentDate,
        totalAmount: d.totalAmount,
        referenceDescription: d.documentPaymentDetails
          .map((dpd) => {
            // nelle righe di pagamento della quietanza ci sono i riferimenti ai documenti sospesi che vengono pagati.
            // Mentre cicliamo le righe per preparare il set per la datagrid, creiamo anche un dataset con le quote
            // delle quietanze raggruppate per documento in sospeso.
            if (dpd.referenceDocument?.id) {
              const item = pendingPaidInFullDocumentQuotas[dpd.referenceDocument.id]
              pendingPaidInFullDocumentQuotas[dpd.referenceDocument.id] = {
                ...item,
                quotas: [...(item?.quotas || []), { paidInFullDocumentId: d.id, amount: dpd.amount, documentNumber: d.numberExtended }],
              }
            }
            return dpd.referenceDocument?.numberExtended
          })
          .join('; ')
          .toString(),
      })) ?? []

    return { paidInFullPendingDocumentsRows, pendingPaidInFullDocumentQuotas }
  }, [dataPaidInFullPendingDocuments])

  const onDeletePaidInFullPendingDocumentHandler = useCallback(
    (documentId: string) => {
      deleteDocument({ variables: { tenantId: tenantId, id: documentId } })
    },
    [tenantId, deleteDocument],
  )

  return (
    <FormLayoutDrawer //
      open={open}
      onClose={onClose}
      title={title}
      width={md ? '60%' : '100%'}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={
            localDocumentId
              ? undefined
              : {
                  disabled: loadings,
                  clickHandler: submitButtonHandler,
                  text: t('commons.confirm'),
                }
          }
          cancelButton={{ clickHandler: () => onClose(), text: t('commons.close') }}
        />
      }
    >
      {cashDeskId !== null ? (
        <Grid container rowSpacing={2}>
          <Grid item xs={12}>
            <HICTitleSmall>{t('pms.accounting.documents.owner_title')}</HICTitleSmall>
            <Controller<PaymentDocumentFormSchema, 'customer'> //
              name="customer"
              control={control}
              defaultValue={{
                id: '',
                countryCode: '',
                address: '',
                splitPayment: false,
              }}
              render={({ field }) => (
                <DocumentCustomerInput //
                  referenceCustomers={[referenceCustomer]}
                  value={field.value}
                  onChange={(v) => {
                    setLocalCustomerId(v.id)
                    field.onChange(v)
                  }}
                  error={errors.customer}
                  readonly={mode === 'VIEW'}
                  customerCurrentData={null}
                  syncData={false}
                  dataSynched={() => {
                    console.log('synched')
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider light />
          </Grid>
          <Grid item xs={12}>
            <HICTitleSmall>{t('pms.accounting.documents.payment_title')}</HICTitleSmall>
          </Grid>
          <Grid item xs={12}>
            <Controller<PaymentDocumentFormSchema, 'payment'> //
              name="payment"
              control={control}
              render={({ field }) => (
                <PaymentSimpleInput //
                  readonly={mode === 'VIEW'}
                  error={errors.payment}
                  value={
                    getValues('payment') || field.value
                      ? {
                          paymentActiveId: getValues('payment.paymentActiveId') || field.value?.paymentActiveId || null,
                          amount: getValues('payment.amount') || field.value.amount || null,
                          editable: getValues('payment.editable') || field.value?.editable,
                        }
                      : null
                  }
                  payments={
                    isDraft
                      ? paymentActivesAllowed.filter((p) => p.paymentActiveId === defaultPaymentActiveWithGateway)
                      : paymentActivesAllowed.filter((p) => p.paymentActiveId !== defaultPaymentActiveWithGateway)
                  }
                  onChange={(value) => field.onChange(value)}
                  onAmountBlur={onPaymentBlurHandler}
                  amountLabel={isDraft ? t('commons.to_pay_amount') : undefined}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider light />
          </Grid>
          {mode === 'NEW' ? (
            <>
              <Grid item xs={12}>
                <HICTitleSmall>{t('pms.accounting.documents.unpaid_documnts_title')}</HICTitleSmall>
              </Grid>
              <Grid item xs={12}>
                <Controller<PaymentDocumentFormSchema, 'documentPayments'> //
                  name="documentPayments"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PaymentDistribution //
                      unpaidDocuments={unpaidDocuments?.documents ?? []}
                      paidAmount={paidAmount}
                      unpaidPaymentActives={unpaidPaymentActives}
                      onTotalPaidAmountChange={(v) => (isDraft ? setValue('payment.amount', v) : undefined)}
                      onChange={field.onChange}
                      documentIdOnTop={comeFromUnpaidDocumentId}
                      error={fieldState.error ? t('pms.accounting.documents.payment_distribution.exceeded_amount') : undefined}
                      paidInFullDocumentsQuotas={pendingPaidInFullDocumentQuotas}
                    />
                  )}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <HICTitleSmall>{t('pms.accounting.documents.related_documents_title')}</HICTitleSmall>
              </Grid>
              <Grid item xs={12}>
                <ReferenceDocumentsList
                  values={dataPaidInFullDocument?.documents?.[0]?.documentPaymentDetails
                    .filter((x) => x.referenceDocument)
                    .map((i) => ({
                      documentNumber: i.referenceDocument?.numberExtended ?? '',
                      documentDate: i.referenceDocument?.documentDate,
                      amount: i.amount,
                    }))}
                />
                {isDraft && <Typography variant="body">{t('pms.accounting.documents.paid_in_full.annotation')}</Typography>}
              </Grid>
            </>
          )}
          {mode === 'NEW' && (
            <>
              <Grid item xs={12}>
                <HICTitleSmall>{t('pms.accounting.documents.pending_paid_in_full_documnts_title')}</HICTitleSmall>
              </Grid>
              <Grid item xs={12}>
                <PaidInFullPendingDocumentsGrid rows={paidInFullPendingDocumentsRows} onDelete={onDeletePaidInFullPendingDocumentHandler} />
              </Grid>
            </>
          )}
        </Grid>
      ) : (
        <HICError>{t('pms.cash_desk_input.no_cash_desk_available')}</HICError>
      )}
    </FormLayoutDrawer>
  )
}
